import { extend } from "dayjs";

/**
 * How an asset's value is arrived at. Mirrors the backend enum and the
 * FamilyJewels spreadsheet's "Val type" column.
 *
 * This is a provenance classifier, not an asset category: cash, a life policy
 * and a shotgun are all `attested` because a third party states the number.
 */
/** Statutory exempt-property categories. Mirrors the backend enum. */
export enum EptlCategory {
  HOUSEHOLD = 'household',
  BOOKS_MEDIA = 'books_media',
  FARM = 'farm',
  VEHICLE = 'vehicle',
  MONEY = 'money',
  CHILD_SETOFF = 'child_setoff',
}

export enum ValuationBasis {
  MARKET_QUOTED = 'market_quoted',
  ATTESTED = 'attested',
  APPRAISED = 'appraised',
  STATEMENT = 'statement',
}

export interface CreateAssetDto {
  estateIdentifier: string;
  ref: string;
  itemNumber: string;
  marketValue: number;
  estimatedValue: number;
  realizedValue: number;
  internalCashlessDistributionAmount?: number;
  estateValueIfInWill?: number;
  estateValueIfJointlyOwned?: number;
  cashReceived?: number;
  percentageAwayFromEstValue?: number;
  percentJointlyOwned?: number;
  portfolioId: number;
  internalDistributionAmount?: number;
  liabilityBalance: number;
  
  // Existing Fields
  name: string;
  category: string; // 'B', 'R', 'F', 'A'
  location: string;
  locationDetail?: string;
  description: string;
  secondaryIdentifier?: string;
  coveredInWill?: boolean;
  jointlyOwned?: boolean;
  EPTLExemption?: boolean;
  isSmallEstate?: boolean;
  propertyType: string; // 'Equity', 'RealEstate'
  equity: string;
  ticker?: string;
  originalQuantityOwned: number;
  priorDayClosingPrice?: number;
  currentQuantityOwned: number;
  dateOfPurchase: string; // ISO Date String
  costBasis: number;
  lease?: boolean;
  liens: number;
  netOfLien?: number;
  valueType: string;
  valueSource: string;
  valuationBasis?: ValuationBasis;
  originalMarketValue?: number;
  eptlCategory?: EptlCategory | null;
  status: string;
  discountToMarket: number;
  familyDiscount: number;
  internalCashlessDistribution?: boolean;
  internalCashDistribution?: boolean;
  internalCashDistributionAmount?: number;
  sold?: boolean;
  donate?: boolean;
}


export interface PostLiability {
  asset: string; // Reference to the associated asset
  assetName?: string; // Optional: To display asset name in liabilities list/details
  description: string;
  amount: string;
  source: string;
  name: string;
  // Add other liability-related fields as necessary
}

  export interface Liability {
    uid: string; // or number, ensure consistency
    estateId: string;
    asset: string; // Reference to the associated asset
    assetName?: string; // Optional: To display asset name in liabilities list/details
    description: string;
    amount: string;
    source: string;
    name: string;
    createdAt?: Date;
    // Add other liability-related fields as necessary
  }
  export interface LiabilitiesResponse {
    data: Liability[];
    total: number;
    page: number;
    pageSize: number;
    // Add other response fields as necessary
  }
  
  export interface NotificationItem {
    id: number;
    uid: string;
    title: string;
    description: string;
    type: 'info' | 'warning' | 'error' | 'success';
    isRead: boolean;
    createdAt: string;
  }
  
  export interface PaginatedNotifications {
    data: NotificationItem[];
    total: number;
    page: number;
    lastPage: number;
  }
export type RecurrenceUnit = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface CashFlow {
  id: string;
  estateId: string;
  name: string;
  description: string;
  type: 'Inbound' | 'Outbound';
  amount: number;
  createdAt?: Date;
  date?: string;
  /** Links are addressed by uid. Names come back read-only for display. */
  assetId?: string | null;
  assetName?: string | null;
  liabilityId?: string | null;
  liabilityName?: string | null;
  participantId?: string | null;
  participantName?: string | null;
  accountNo?: string;
  isRecurring?: boolean;
  recurrenceInterval?: RecurrenceUnit | null;
  /** Units between occurrences: quarterly is monthly with a count of 3. */
  recurrenceCount?: number;
  /** 1-12, or null when the schedule runs all year. Seasons may wrap the year end. */
  seasonStartMonth?: number | null;
  seasonEndMonth?: number | null;
  nextDueDate?: string | null;
}

export interface CashFlowsResponse {
  data: CashFlow[];
  total: number;
  page: number;
  limit: number;
}

// types.ts
export interface User {
  id: number;
  email: string;
  pendingEmail?: string | null;
  uid: string;
  firstName: string;
  middleName: string | null;
  lastName: string | null;
  sex: string | null;
  prefix: string | null;
  suffix: string | null;
  dob: string | null;
  representative: string | null;
  maritalStatus: string | null;
  deceased: boolean | null;
  specialCircumstance: string | null;
  terms: string | null;
  stage: string | null;
  profileComplete: boolean | null;
  /** Whether global search reaches past the active estate. */
  searchAllEstates?: boolean;
  userType: string | null;
  createdOn: string | null;
  updatedOn: string | null;
  updatedBy: string | null;
  lastLogin: string | null;
  lastLoginIp: string | null;
  lastLoginDevice: string | null;
  lastLoginLocation: string | null;
  lastLoginBrowser: string | null;
  otherLoginInfo: string | null;
  lastPasswordChange: string | null;
  passwordChangeCount: number | null;
  isAdmin: boolean;
  isSuperAdmin: boolean | null;
  isDeleted: boolean | null;
  isLocked: boolean | null;
  isVerified: boolean | null;
  activationCode: string | null;
  activationExpiry: string | null;
  profilePic: string | null;
  active: boolean | null;
  idBack: string | null;
  idFront: string | null;
  idType: string | null;
  idNumber: string | null;
  roles: string;
  ssn: string | null;
  dateOfDeath: string | null;
  phone: string | null;
}

export interface EstateOwner extends User {
  addresses?: UpdateAddress[];
}



// types.ts

export interface ChangeDetails {
  after?: Record<string, any>;
  before?: Record<string, any>;
  updatedColumns?: string[];
}

export interface AuditEvent {
  id: string;
  timestamp: string;
  entityName: string;
  changes: ChangeDetails;
  operation: string;
  severity: string;
  user?: User;
  accessLevel: string;
  tags: string[];
}


// types.ts

export interface Address {
  addressLine1: string;
  addressLine2?: string;
  addressType: string;
  city: string;
  state: string;
  zipCode: string;
  postalCode?: string;
  county?: string;
  country?: string;
  primaryPhone?: string;
  alternatePhone1?: string;
  alternatePhone2?: string;
}
export interface UpdateAddress extends Address {
  uid?: string;
}
export interface AddParticipantPayload {
  
  email: string;
  firstName: string;
  middleName?: string | null;
  lastName: string | null;
  addresses: Address[];
  sex?: string | null;
  prefix?: string | null;
  suffix?: string | null;
  dob?: string | null;
  representative?: string | null;
  maritalStatus?: string | null;
  deceased?: boolean | null;
  specialCircumstance?: string | null;
  profileComplete?: boolean | null;
  userType?: string | null;
  relatedTo: string; // UID of the related user
  natureOfRelation: string;
  domicileAddress?: Address;
  mailingAddress?: Address;
  presentAddress?: Address;
  ssn?: string | null;
  dateOfDeath?: string; // ISO date string if deceased
}


export interface ParticipantResponse extends Participant {
  uid: string;
  addresses: UpdateAddress[];
  estates: Estate[];
  relationships: any[];
  relationship: any[]
}


export interface EditParticipantPayload extends Partial<AddParticipantPayload> {

  addresses?: UpdateAddress[];
}

export interface Participant extends User{
  // ... existing fields ...
  relatedTo: string;
  natureOfRelation: string;
  addresses: Address[];
   domicileAddress: Address;
   mailingAddress?: Address;
   presentAddress?: Address;
   // ... other fields ...
}

export interface Executor extends Participant {
  // Additional fields if any
}


export interface Estate {
  id: number;
  uuid: string;
  estateName: string;
  description: string;
  /** Two-letter code for the state whose law governs the estate. */
  state?: string | null;
  /** County the petition is filed in. */
  county?: string | null;
  /**
   * The estate's own address, which is not the decedent's: the estate is a
   * legal person in its own right, and its post goes wherever it is
   * administered from.
   */
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  zipCode?: string | null;
  /** The estate's own IRS tax id, formatted NN-NNNNNNN. */
  ein?: string | null;
  createdAt: Date;
  updatedAt: string;
  participants: Participant[];
  executor: User[];
  users: User[];
  guests: User[];
  invites: User[];
  address?: string;
  estateOwner: EstateOwner;
  assets: any[]; // TODO: define more accurately based on API
}

export interface EstateResponse {
  particpants: ParticipantResponse[];
  
}
export interface ExecutorInvitePayload {
  name: string;
  email: string;
}

export interface ExecutorInviteResponse {
  message: string;
}

export interface ApiErrorResponse {
  statusCode: number;
  message: string;
}

/**
  * Editing an estate edits the estate.
  */
export interface EditEstatePayload {
  description: string;
  estateName: string;
  state?: string;
  county?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  zipCode?: string;
  /** Nine digits, formatted NN-NNNNNNN. Empty clears it. */
  ein?: string;
}
export interface EditEstateResponse extends Estate {}

export interface CreateEstatePayload {
  dob: string; // "2022-09-27 18:00:00.000"
  description: string;
  estateName: string;
  firstName: string;
  isDeceased: boolean;
  lastName: string;
  middleName: string;
  prefix: string;
  sex: string;
  suffix: string;
  state?: string;
  county?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  zipCode?: string;
  ein?: string;
  phone?: string;
  maritalStatus?: string;
  citizenship?: string;
  addresses?: UpdateAddress[];
}
export interface CreateEstateResponse extends Estate {}

/** The estate owner, edited on its own. */
export interface EditEstateOwnerPayload {
  email?: string;
  prefix?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  suffix?: string;
  sex?: string;
  dob?: string;
  phone?: string;
  maritalStatus?: string;
  citizenship?: string;
  isDeceased?: boolean;
  dateOfDeath?: string;
  placeOfDeathName?: string;
  placeOfDeathAddress?: string;
  ssn?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  county?: string;
  addresses?: UpdateAddress[];
}

export interface AssetsResponse {
  data: Asset[];
  total: number;
  totalValue: number;
  totalValueLastMonth: number;
  averageAssetValue: number;
  // categoryDistribution: { [category: string]: number };
  page: number;
  lastPage: number;
}

export interface  Portfolio {
  ticker?: string;
  internalCashDistributionAmount?: number;
  equity?: string;
  originalQuantityOwned?: number;
  priorDayClosingPrice?: number;
  dateOfPurchase?: string; 
  currentQuantityOwned?: string;
  costBasis?: number;
}
export interface Asset {
  id: number;
  uid:string;
  estateIdentifier: string;
  name: string;
  coveredInWill: boolean;
  ref: string;
  itemNumber: string;
  EPTLExemption: boolean;
  isSmallEstate: boolean;
  location: string;
  category: string;
  description: string;
  secondaryIdentifier: string;
  marketValue: number;
  lease: boolean;
  liens: number;
  netOfLien: number;
  valueType: string;
  valueSource: string;
  valuationBasis?: ValuationBasis;
  originalMarketValue?: number;
  eptlCategory?: EptlCategory | null;
  status: string;
  discountToMarket: number;
  estimatedValue: number;
  realizedValue: number;
  familyDiscount: number;
  internalCashlessDistribution: boolean;
  internalCashlessDistributionAmount: number;
  internalCashDistribution: boolean;
  estateValueIfInWill: number;
  estateValueIfJointlyOwned: number;
  jointlyOwned: boolean;
  sold: boolean;
  donate: boolean;
  givenToFriend: boolean;
  thrownOut: boolean;
  cashReceived: number;
  percentageAwayFromEstValue: number;
  percentJointlyOwned: number;
  portfolioId: number;
  liabilityBalance: number;
  createdAt: Date;
  updatedAt: Date;
  costBasis: number;
  currentQuantityOwned: number;
  realisedExternalSale?: number;
  internalDistributionAmount?: number;
  estateValue?: number;
  /** An amount, not a flag: the value counted toward probate administration. */
  toBeCountedInProbate?: number;
  countedInProbateValue?: number;
  familyDiscountedValue?: number;
  jointlyOwnedValue?: number;
  smallEstateValue?: number;
  estateLeftToBeDistributed?: number;
  /** Shares of the estate, as fractions of 1. */
  percantageOfEstateAtDiscount?: number;
  shareOfEstateDiscount?: number;
  shareOfTotalEstateDiscount?: number;
  EPTLExemptionAmount?: number;
  residualEPTLExemptionAmount?: number;

  portfolio?: Portfolio;

  ticker?: string;
  internalCashDistributionAmount?: number;
  equity?: string;
  originalQuantityOwned?: number;
  priorDayClosingPrice?: number;
  dateOfPurchase?: string;

}
export interface AssetsResponse {
  data: Asset[];
  total: number;
  totalValue: number;
  totalValueLastMonth: number; // For comparison
  page: number;
  lastPage: number;
}

// PDF Field Mapping Types
export interface PdfFieldMapping {
  id: number;
  uid: string;
  pdfFieldName: string;           // Original field name from PDF
  databaseFieldName: string | null; // Mapped database field (e.g., "user.firstName")
  relatedEntity: string | null;   // Entity type (e.g., "user", "estate", "asset")
  /**
   * Which row to use when the entity holds several: "[1]" by position, or
   * "[field=value]" by a match. Null means the first row.
   */
  entitySelector?: string | null;
  pageNumber: number;
  type: string;                   // "TextField", "CheckBox", etc.
  surroundingText: string;        // Context text near the field
  rect: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  defaultValue: string | null;
  template?: string | null;      // Template expression (supersedes databaseFieldName), e.g. "{{user.firstName}} {{user.lastName}}"
  probateDocId?: string;
}

export interface MappableEntityField {
  value: string;
  label: string;
}

export interface MappableEntity {
  label: string;
  fields: MappableEntityField[];
  /**
   * True when an estate holds several of these, so a mapping has to say which
   * one it means. Estates, the owner and the decedent are singular; assets,
   * participants and addresses are not.
   */
  multiple?: boolean;
  /**
   * Fields worth offering as a way to pick a row, beyond position. Used for
   * people, where "the surviving spouse" is stabler than "the second one".
   */
  selectBy?: MappableEntityField[];
  /**
   * Kept so saved mappings still read back and existing documents keep
   * filling, but no longer offered when choosing an entity.
   */
  hidden?: boolean;
}

/**
 * The entities offered in the entity picker, in catalogue order.
 *
 * `current` is the entity already saved on the mapping being edited. Passing it
 * keeps a hidden entity visible for as long as that mapping still uses it, so
 * an older mapping shows what it points at instead of an empty box.
 */
export const mappableEntityOptions = (
  current?: string,
): Array<[string, MappableEntity]> =>
  Object.entries(MAPPABLE_ENTITIES).filter(
    ([key, entity]) => !entity.hidden || key === current,
  );

export const MAPPABLE_ENTITIES: Record<string, MappableEntity> = {
  owner: {
    label: 'Estate Owner (the legal person whose estate this is)',
    fields: [
      { value: 'prefix', label: 'Prefix' },
      { value: 'firstName', label: 'First Name' },
      { value: 'middleName', label: 'Middle Name' },
      { value: 'lastName', label: 'Last Name' },
      { value: 'suffix', label: 'Suffix' },
      { value: 'email', label: 'Email' },
      { value: 'phone', label: 'Phone' },
      { value: 'sex', label: 'Sex' },
      { value: 'dob', label: 'Date of Birth' },
      { value: 'maritalStatus', label: 'Marital Status' },
      { value: 'ssn', label: 'Social Security Number' },
      { value: 'citizenship', label: 'Citizenship' },
      { value: 'dateOfDeath', label: 'Date of Death' },
      { value: 'placeOfDeathName', label: 'Place of Death' },
      { value: 'placeOfDeathAddress', label: 'Place of Death Address' },
      { value: 'willExists', label: 'Will Exists' },
      { value: 'addresses[0].addressLine1', label: 'Address Line 1' },
      { value: 'addresses[0].addressLine2', label: 'Address Line 2' },
      { value: 'addresses[0].city', label: 'City' },
      { value: 'addresses[0].state', label: 'State' },
      { value: 'addresses[0].county', label: 'County' },
      { value: 'addresses[0].zipCode', label: 'Zip Code' },
      { value: 'addresses[0].country', label: 'Country' },
      { value: 'fullName', label: 'Full Name (Prefix First Middle Last Suffix)' },
      { value: 'fullAddress', label: 'Full Address (one line)' },
    ]
  },
  participant: {
    label: 'Beneficiary / Participant (inherits, e.g. a child)',
    multiple: true,
    selectBy: [
      { value: 'relationshipToDecedent', label: 'Relationship to Decedent' },
    ],
    fields: [
      { value: 'firstName', label: 'First Name' },
      { value: 'middleName', label: 'Middle Name' },
      { value: 'lastName', label: 'Last Name' },
      { value: 'email', label: 'Email' },
      { value: 'phone', label: 'Phone' },
      { value: 'dob', label: 'Date of Birth' },
      { value: 'relationshipToDecedent', label: 'Relationship to Decedent' },
      { value: 'underDisability', label: 'Under Disability' },
      { value: 'addresses[0].addressLine1', label: 'Address Line 1' },
      { value: 'addresses[0].city', label: 'City' },
      { value: 'addresses[0].state', label: 'State' },
      { value: 'addresses[0].zipCode', label: 'Zip Code' },
      { value: 'fullName', label: 'Full Name (Prefix First Middle Last Suffix)' },
      { value: 'fullAddress', label: 'Full Address (one line)' },
    ]
  },
  executor: {
    label: 'Executor',
    multiple: true,
    selectBy: [
      { value: 'relationshipToDecedent', label: 'Relationship to Decedent' },
    ],
    fields: [
      { value: 'prefix', label: 'Prefix' },
      { value: 'firstName', label: 'First Name' },
      { value: 'middleName', label: 'Middle Name' },
      { value: 'lastName', label: 'Last Name' },
      { value: 'suffix', label: 'Suffix' },
      { value: 'email', label: 'Email' },
      { value: 'phone', label: 'Phone' },
      { value: 'relationshipToDecedent', label: 'Relationship to Decedent' },
      { value: 'addresses[0].addressLine1', label: 'Address Line 1' },
      { value: 'addresses[0].city', label: 'City' },
      { value: 'addresses[0].state', label: 'State' },
      { value: 'addresses[0].zipCode', label: 'Zip Code' },
      { value: 'fullName', label: 'Full Name (Prefix First Middle Last Suffix)' },
      { value: 'fullAddress', label: 'Full Address (one line)' },
    ]
  },
  liability: {
    label: 'Liability',
    multiple: true,
    selectBy: [
      { value: 'name', label: 'Name' },
    ],
    fields: [
      { value: 'name', label: 'Name' },
      { value: 'description', label: 'Description' },
      { value: 'amount', label: 'Amount' },
      { value: 'dueDate', label: 'Due Date' },
    ]
  },
  // Renamed to `date`. Still resolved, so mappings written against the old
  // name keep filling on a database the migration has not reached.
  computed: {
    label: 'Computed',
    hidden: true,
    fields: [
      { value: 'today', label: "Today's Date" },
    ]
  },
  user: {
    label: 'User',
    fields: [
      { value: 'firstName', label: 'First Name' },
      { value: 'lastName', label: 'Last Name' },
      { value: 'email', label: 'Email' },
      { value: 'phone', label: 'Phone' },
      { value: 'address.addressLine1', label: 'Address Line 1' },
      { value: 'address.city', label: 'City' },
      { value: 'address.state', label: 'State' },
      { value: 'address.zipCode', label: 'Zip Code' },
      { value: 'fullName', label: 'Full Name (Prefix First Middle Last Suffix)' },
      { value: 'fullAddress', label: 'Full Address (one line)' },
    ]
  },
  estate: {
    label: 'Estate',
    fields: [
      { value: 'estateName', label: 'Estate Name' },
      { value: 'description', label: 'Description' },
      { value: 'ein', label: 'EIN (estate tax id)' },
      // The estate's own address, not the decedent's. Theirs is under Decedent.
      { value: 'addressLine1', label: 'Estate Address Line 1' },
      { value: 'addressLine2', label: 'Estate Address Line 2' },
      { value: 'city', label: 'Estate City' },
      { value: 'state', label: 'Estate State' },
      { value: 'county', label: 'Estate County' },
      { value: 'zipCode', label: 'Estate Zip Code' },
      { value: 'createdAt', label: 'Created Date' },
    ]
  },
  address: {
    label: 'Address',
    multiple: true,
    fields: [
      { value: 'addressLine1', label: 'Address Line 1' },
      { value: 'addressLine2', label: 'Address Line 2' },
      { value: 'city', label: 'City' },
      { value: 'state', label: 'State' },
      { value: 'zipCode', label: 'Zip Code' },
      { value: 'county', label: 'County' },
      { value: 'country', label: 'Country' },
    ]
  },
  asset: {
    label: 'Asset',
    multiple: true,
    selectBy: [
      { value: 'name', label: 'Asset Name' },
      { value: 'category', label: 'Asset Type' },
      { value: 'ref', label: 'Reference' },
    ],
    fields: [
      { value: 'name', label: 'Asset Name' },
      { value: 'description', label: 'Description' },
      { value: 'marketValue', label: 'Market Value' },
      { value: 'estimatedValue', label: 'Estimated Value' },
      { value: 'realisedExternalSale', label: 'Realized External Sale' },
      { value: 'residualEPTLExemptionAmount', label: 'Residual EPTL Exemption' },
      { value: 'estateValue', label: 'Estate Value' },
      { value: 'jointlyOwnedValue', label: 'Jointly Owned Value' },
      { value: 'smallEstateValue', label: 'Small Estate Value' },
      { value: 'countedInProbateValue', label: 'Counted in Probate Value' },
      { value: 'cashReceived', label: 'Cash Received' },
      { value: 'internalDistributionAmount', label: 'Internal Distribution Amount' },
      { value: 'internalCashDistributionAmount', label: 'Internal Cash Distribution Amount' },
      { value: 'estateLeftToBeDistributed', label: 'Estate Left To Be Distributed' },
      { value: 'EPTLExemptionAmount', label: 'EPTL Exemption Amount' },
      { value: 'familyDiscountedValue', label: 'Family Discounted Value' },
      { value: 'shareOfEstateDiscount', label: 'Share of Estate Discount' },
      { value: 'shareOfTotalEstateDiscount', label: 'Share of Total Estate Discount' },
      { value: 'percentageAwayFromEstValue', label: 'Percentage Away From Est Value' },
      { value: 'liens', label: 'Liens' },
      { value: 'netOfLien', label: 'Net of Lien' },
      { value: 'location', label: 'Location' },
      { value: 'category', label: 'Category' },
    ]
  },
  totals: {
    label: 'Estate Totals',
    fields: [
      { value: 'estateValue', label: 'Total Estate Value (gross)' },
      // What the court is actually asked for on a petition: the gross estate
      // less the property set aside by statute.
      { value: 'probateEstateValue', label: 'Probate Estate Value (after exempt property)' },
      { value: 'exemption.totalExempt', label: 'Total Exempt Property' },
      { value: 'exemption.totalExcess', label: 'Exempt Property Over the Cap' },
      { value: 'exemption.statute', label: 'Exempt Property Statute (e.g. EPTL 5-3.1)' },
      { value: 'totalAssets', label: 'Total Assets Count' },
      { value: 'totalLiabilities', label: 'Total Liabilities Count' },
      { value: 'totalInboundCashflow', label: 'Total Inbound Cashflow' },
      { value: 'totalOutboundCashflow', label: 'Total Outbound Cashflow' },
      { value: 'marketValueSum', label: 'Total Market Value' },
      { value: 'estimatedValueSum', label: 'Total Estimated Value' },
      { value: 'realizedExternalSaleSum', label: 'Total Realized External Sale' },
      { value: 'EPTLExemptionAmountSum', label: 'Total EPTL Exemption Amount' },
      { value: 'residualEPTLExemptionAmountSum', label: 'Total Residual EPTL Exemption' },
      { value: 'estateValueSum', label: 'Total Estate Value Sum' },
      { value: 'jointlyOwnedValueSum', label: 'Total Jointly Owned Value' },
      { value: 'smallEstateValueSum', label: 'Total Small Estate Value' },
      { value: 'countedInProbateValueSum', label: 'Total Counted in Probate Value' },
      { value: 'cashReceivedSum', label: 'Total Cash Received' },
      { value: 'internalDistributionAmountSum', label: 'Total Internal Distribution Amount' },
      { value: 'internalCashLessDistributionAmountSum', label: 'Total Internal Cashless Distribution' },
      { value: 'estateLeftToBeDistributed', label: 'Total Estate Left To Be Distributed' },
      { value: 'liensSum', label: 'Total Liens' },
      { value: 'netLiensSum', label: 'Total Net Liens' },
      { value: 'shareOfEstateDiscountSum', label: 'Total Share of Estate Discount' },
      { value: 'shareOfTotalEstateDiscountSum', label: 'Total Share of Total Estate Discount' },
      { value: 'totalLiabilityValue', label: 'Total Liability Value' },
      { value: 'totalCashflowValue', label: 'Total Cashflow Value' },
    ]
  },
  // Dates a form asks for that nobody types in. Every other date on a court
  // form belongs to a person or an asset, so it is mapped from there: a date of
  // death from the Estate Owner, a due date from a Liability.
  date: {
    label: 'Date',
    fields: [
      { value: 'today', label: "Today's Date (when the document is generated)" },
    ]
  },
  // The Estate Owner is the person these forms name, and offering Benefactor
  // beside it only asks which of the two a field means. Still resolved, so
  // mappings already written against it keep filling.
  benefactor: {
    label: 'Benefactor (owns the estate, e.g. a parent)',
    hidden: true,
    multiple: true,
    selectBy: [
      { value: 'relationshipToDecedent', label: 'Relationship to Decedent' },
    ],
    fields: [
      { value: 'firstName', label: 'First Name' },
      { value: 'middleName', label: 'Middle Name' },
      { value: 'lastName', label: 'Last Name' },
      { value: 'email', label: 'Email' },
      { value: 'phone', label: 'Phone' },
      { value: 'dob', label: 'Date of Birth' },
      { value: 'relationshipToDecedent', label: 'Relationship to Decedent' },
      { value: 'underDisability', label: 'Under Disability' },
      { value: 'addresses[0].addressLine1', label: 'Address Line 1' },
      { value: 'addresses[0].city', label: 'City' },
      { value: 'addresses[0].state', label: 'State' },
      { value: 'addresses[0].zipCode', label: 'Zip Code' },
      { value: 'fullName', label: 'Full Name (Prefix First Middle Last Suffix)' },
      { value: 'fullAddress', label: 'Full Address (one line)' },
    ]
  }
};